import type { Locale, PriceQuote } from "@/catalog";

interface CheckoutApiBody {
  checkoutUrl?: string;
  error?: { code?: string };
  quote?: PriceQuote;
}

interface ExpectedCheckoutQuote {
  priceCents: number;
  slotIndex: number;
}

export type CheckoutUiState = "idle" | "opening" | "error" | "cancelled" | "returned";

interface CheckoutPageShowTarget {
  addEventListener(type: "pageshow", listener: (event: PageTransitionEvent) => void): void;
  removeEventListener(type: "pageshow", listener: (event: PageTransitionEvent) => void): void;
}

export class CheckoutPriceChangedError extends Error {
  constructor(readonly quote: PriceQuote) {
    super("checkout_price_changed");
    this.name = "CheckoutPriceChangedError";
  }
}

export function checkoutStateAfterPageRestore(state: CheckoutUiState): CheckoutUiState {
  return state === "opening" ? "returned" : state;
}

export function checkoutReturnOutcome(search: string): "success" | "cancelled" | null {
  const outcome = new URLSearchParams(search).get("checkout");
  return outcome === "success" || outcome === "cancelled" ? outcome : null;
}

export function listenForCheckoutPageRestore(
  onRestore: () => void,
  target: CheckoutPageShowTarget = window,
): () => void {
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) onRestore();
  };
  target.addEventListener("pageshow", onPageShow);
  return () => target.removeEventListener("pageshow", onPageShow);
}

function browserStorage(): Storage | undefined {
  try {
    return globalThis.sessionStorage;
  } catch {
    return undefined;
  }
}

export function checkoutIdempotencyKey(
  opportunitySlug: string,
  rotate = false,
  storage = browserStorage(),
): string {
  const storageKey = `refound:checkout:${opportunitySlug}`;
  try {
    const existing = storage?.getItem(storageKey);
    if (!rotate && existing && /^[A-Za-z0-9._:-]{8,128}$/.test(existing)) return existing;
    const created = crypto.randomUUID();
    storage?.setItem(storageKey, created);
    return created;
  } catch {
    return crypto.randomUUID();
  }
}

async function checkoutAttempt(
  locale: Locale,
  opportunitySlug: string,
  idempotencyKey: string,
  fetcher: typeof fetch,
  expectedQuote?: ExpectedCheckoutQuote,
): Promise<{ response: Response; body: CheckoutApiBody | null }> {
  const response = await fetcher("/api/checkout", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "idempotency-key": idempotencyKey,
    },
    body: JSON.stringify({
      locale,
      opportunitySlug,
      ...(expectedQuote ? {
        expectedPriceCents: expectedQuote.priceCents,
        expectedSlotIndex: expectedQuote.slotIndex,
      } : {}),
    }),
  });
  const body = (await response.json().catch(() => null)) as CheckoutApiBody | null;
  return { response, body };
}

async function requestCheckoutUrlInternal(
  locale: Locale,
  opportunitySlug: string,
  fetcher: typeof fetch = globalThis.fetch,
  storage = browserStorage(),
  expectedQuote?: ExpectedCheckoutQuote,
): Promise<string> {
  let result = await checkoutAttempt(
    locale,
    opportunitySlug,
    checkoutIdempotencyKey(opportunitySlug, false, storage),
    fetcher,
    expectedQuote,
  );

  // A released, expired, or already-confirmed reservation is safe to replace.
  // Ambiguous network/5xx failures deliberately retain the same key.
  if (
    result.response.status === 409 &&
    result.body?.error?.code === "idempotency_key_terminal"
  ) {
    result = await checkoutAttempt(
      locale,
      opportunitySlug,
      checkoutIdempotencyKey(opportunitySlug, true, storage),
      fetcher,
      expectedQuote,
    );
  }

  if (
    result.response.status === 409
    && result.body?.error?.code === "price_changed"
    && result.body.quote
  ) {
    throw new CheckoutPriceChangedError(result.body.quote);
  }

  if (!result.response.ok || !result.body?.checkoutUrl) {
    throw new Error("checkout_unavailable");
  }
  return result.body.checkoutUrl;
}

export function requestCheckoutUrl(
  locale: Locale,
  opportunitySlug: string,
  fetcher: typeof fetch = globalThis.fetch,
  storage = browserStorage(),
): Promise<string> {
  return requestCheckoutUrlInternal(locale, opportunitySlug, fetcher, storage);
}

export function requestCheckoutUrlAtQuote(
  locale: Locale,
  opportunitySlug: string,
  expectedQuote: ExpectedCheckoutQuote,
  fetcher: typeof fetch = globalThis.fetch,
  storage = browserStorage(),
): Promise<string> {
  return requestCheckoutUrlInternal(locale, opportunitySlug, fetcher, storage, expectedQuote);
}
